import React, { useState, useEffect, useRef } from "react";
import { Card, Input, Button, Alert, Spin, Statistic, Progress, Descriptions, Badge, Row, Col, Divider, Table, Tag, message, Modal, Radio } from "antd";
import { ReloadOutlined, SearchOutlined, CheckCircleOutlined, LineChartOutlined, ClockCircleOutlined, ToolOutlined, DownloadOutlined, RedoOutlined } from "@ant-design/icons";
import { authAxios } from "../../components/utils/session_utils";

const { Search } = Input;

// Job progress monitor props
export interface JobProgressMonitorProps {
  jobId: string;
  refreshInterval?: number;
  onClose?: () => void;
  compact?: boolean;
  // New prop to determine if we should show recent jobs when no job ID is provided
  showRecentJobs?: boolean;
}

// Removed deriveStatusFromMessage - now trusting backend status completely

// Shared status processing function for consistency between main table and details
const processJobStatus = (rawStatus: any, operationId: string, source: 'individual' | 'recent' = 'individual') => {
  // Unified status processing logic
  let status: string;
  let message: string;
  let fullData: any;

  if (typeof rawStatus === 'string') {
    status = rawStatus;
    message = '';
    fullData = { status: rawStatus };
  } else if (rawStatus && typeof rawStatus === 'object') {
    // Handle both response formats consistently
    if (source === 'individual' && rawStatus.status && typeof rawStatus.status === 'object') {
      // Individual job API returns { status: jobData }
      fullData = rawStatus.status;
      status = fullData.status || 'UNKNOWN';
      message = fullData.message || '';
    } else {
      // Direct job data or recent jobs format
      fullData = rawStatus;
      status = rawStatus.status || 'UNKNOWN';
      message = rawStatus.message || '';
    }
  } else {
    status = 'UNKNOWN';
    message = '';
    fullData = { status: 'UNKNOWN' };
  }

  // Removed verbose logging

  return { status, message, fullData };
};

// Helper function to normalize status messages for consistent display
function normalizeStatusMessage(message: string, status: string): string {
  // If message contains manually updated text, replace with standard message
  if (message && message.includes("manually updated")) {
    if (status === "COMPLETED") {
      return "All records processed successfully";
    } else if (status === "SNOWFLAKE_PROCESSED") {
      return "Snowflake processing completed successfully";
    }
  }
  
  // Return original message if no normalization is needed
  return message;
}

// Helper function to format elapsed time in a human-readable way
function formatElapsedTime(startDate: Date, endDate: Date = new Date()): string {
  const diffMs = Math.max(0, endDate.getTime() - startDate.getTime());
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}


const JobProgressMonitor: React.FC<JobProgressMonitorProps> = ({ 
  jobId: propJobId,
  refreshInterval: propRefreshInterval = 5000, // Keep at 5s for responsive active job monitoring
  compact = false,
}) => {
  // State for the search input (used only in standalone mode)
  const [searchJobId, setSearchJobId] = useState<string>("");
  
  // State for the actual job ID being monitored
  const [activeJobId, setActiveJobId] = useState<string>(propJobId || "");
  const activeJobIdRef = useRef<string>(propJobId || "");
  
  const [jobData, setJobData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  
  // Status history for debouncing status changes
  const [statusHistory, setStatusHistory] = useState<{[key: string]: string[]}>({});
  
  // Helper function to prevent status oscillation
  const shouldUpdateStatus = (jobId: string, newStatus: string): boolean => {
    const history = statusHistory[jobId] || [];
    const recentStatuses = history.slice(-3); // Last 3 statuses
    
    // Prevent oscillating between PENDING and COMPLETED
    if (recentStatuses.includes('COMPLETED') && newStatus === 'PENDING') {
      console.log(`🚫 Preventing status regression from COMPLETED to PENDING for ${jobId}`);
      return false;
    }
    
    // Prevent oscillating between PROCESSING and QUEUED  
    if (recentStatuses.includes('PROCESSING') && newStatus === 'QUEUED') {
      console.log(`🚫 Preventing status regression from PROCESSING to QUEUED for ${jobId}`);
      return false;
    }
    
    // Allow the update
    return true;
  };
  
  // Ref to track current recentJobs for interval access
  const recentJobsRef = useRef<any[]>([]);
  
  
  // State for recent jobs - ALWAYS AUTO-REFRESH
  const [recentJobs, setRecentJobs] = useState<any[]>([]);
  const [loadingRecentJobs, setLoadingRecentJobs] = useState<boolean>(false);
  const [recentJobsError, setRecentJobsError] = useState<string | null>(null);
  
  // State for fix job modal
  const [fixStuckJobModalVisible, setFixStuckJobModalVisible] = useState<boolean>(false);
  const [jobToFix, setJobToFix] = useState<any>(null);
  const [fixingJob, setFixingJob] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('COMPLETED');
  
  // State for export and reprocess functionality
  const [loadingExport, setLoadingExport] = useState(false);
  const [selectedJobForExport, setSelectedJobForExport] = useState<string | null>(null);
  const [loadingReprocess, setLoadingReprocess] = useState(false);
  const [selectedJobForReprocess, setSelectedJobForReprocess] = useState<string | null>(null);
  
  
  // NEW: Function to fetch recent jobs
  const openFixJobModal = (job: any) => {
    setJobToFix(job);
    setFixStuckJobModalVisible(true);
  };
  
  // Function to fix a job status
  const fixJobStatus = async (targetStatus: string, jobOverride?: any) => {
    const jobData = jobOverride || jobToFix;
    if (!jobData) return;
    
    setFixingJob(true);
    
    try {
      const operationId = jobData.operationId || activeJobId;
      
      const response = await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/salesforce/fix-stuck-job/${operationId}`,
        data: { targetStatus }
      });
      
      if (response.data.success) {
        if (targetStatus === 'CLEAR_RECORDS') {
          message.success({
            content: (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>✅ Records Cleared Successfully!</div>
                <div>Schedule locks have been released. You can now retry uploading the same file.</div>
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  💡 Tip: Go back to the upload page and try uploading your file again.
                </div>
              </div>
            ),
            duration: 8, // Show longer for clear records
          });
        } else if (targetStatus === 'COMPLETED') {
          message.success('✅ Job marked as completed successfully!');
        } else if (targetStatus === 'CANCELLED') {
          message.success('🚫 Job cancelled successfully!');
        } else {
          message.success(`Job status updated to ${targetStatus}`);
        }
        
        setFixStuckJobModalVisible(false);
        fetchRecentJobs(); // Refresh job list
        
        // If the fixed job is the current job, refresh it
        if (operationId === activeJobId) {
          fetchJobStatus();
        }
        
        // For CLEAR_RECORDS action, reset the UI state to reflect the updated status
        if (targetStatus === 'CLEAR_RECORDS') {
          // Update the current job data to remove the warning message immediately
          if (operationId === activeJobId) {
            setJobData((prevData: any) => ({
              ...prevData,
              status: 'FAILED',
              message: 'Locks cleared successfully. Please reupload your file.',
              endTime: new Date().toISOString()
            }));
          }
          
          // Show a simple popup message
          message.info({
            content: "Locks cleared. Please reupload your file.",
            duration: 4
          });
          
          // Clear any error states
          setError(null);
        }
      } else {
        message.error(response.data.message || 'Failed to update job status');
      }
    } catch (error: any) {
      console.error('Error fixing job:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to update job status');
    } finally {
      setFixingJob(false);
    }
  };

  // Handler for exporting failed records as CSV
  const handleExportFailedRecords = async (operationId: string) => {
    setLoadingExport(true);
    setSelectedJobForExport(operationId);
    
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/salesforce/export-failed-records/${operationId}`,
        responseType: 'blob', // Important for file download
      });
      
      // Create blob and download file
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `failed_records_${operationId}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('✅ Failed records exported successfully!');
      
    } catch (error: any) {
      console.error('Error exporting failed records:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to export failed records');
    } finally {
      setLoadingExport(false);
      setSelectedJobForExport(null);
    }
  };

  // Handler for reprocessing failed records
  const handleReprocessFailedRecords = async (operationId: string) => {
    setLoadingReprocess(true);
    setSelectedJobForReprocess(operationId);
    
    try {
      const response = await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/salesforce/reprocess-failed-records/${operationId}`,
        data: {} // Can add errorTypes filter here if needed
      });
      
      if (response.data.success) {
        message.success({
          content: (
            <div>
              <div>✅ Reprocessing started successfully!</div>
              <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                New job ID: {response.data.retryJobId}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Reprocessing {response.data.recordsToReprocess} failed records
              </div>
            </div>
          ),
          duration: 6,
        });
        
        // Refresh job list to show new retry job
        fetchRecentJobs();
        
        // If monitoring current job, refresh it
        if (operationId === activeJobId) {
          fetchJobStatus();
        }
      } else {
        message.error(response.data.message || 'Failed to start reprocessing');
      }
    } catch (error: any) {
      console.error('Error reprocessing failed records:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to start reprocessing');
    } finally {
      setLoadingReprocess(false);
      setSelectedJobForReprocess(null);
    }
  };
  
  const fetchRecentJobs = async () => {
    setLoadingRecentJobs(true);
    setRecentJobsError(null);
    
    try {
      // Fetching recent jobs
      
      const response = await authAxios({
        method: "GET",
        url: `${process.env.REACT_APP_BACK_END_API}/salesforce/operation-status/recent`,
        params: {
          limit: 20,
          type: 'spot-data' // Only get spot data upload jobs if needed
        }
      });
      
      if (response.data.success) {
        // Transform job data for better display using shared status processor
        const formattedJobs = response.data.jobs.map((job: any) => {
          
          // Use shared status processing function
          const { status, message, fullData } = processJobStatus(job, job.operationId, 'recent');
          
          // ENHANCED: Extract progress data for better UI display
          const successCount = fullData.successCount || 0;
          const failureCount = fullData.failureCount || 0;
          const processedRecords = fullData.processedRecords;
          const safeProcessedRecords = processedRecords !== undefined && processedRecords !== null && !isNaN(processedRecords) 
            ? processedRecords 
            : (successCount + failureCount);
          
          const progressData = {
            processedRecords: safeProcessedRecords,
            totalRecords: fullData.totalRecords || 0,
            percentComplete: fullData.percentComplete !== undefined ? fullData.percentComplete :
              (fullData.totalRecords > 0 && !isNaN(safeProcessedRecords) ? Math.round((safeProcessedRecords / fullData.totalRecords) * 100) : 0),
            successCount: successCount,
            failureCount: failureCount
          };
          
          // Removed verbose logging
          
          // Determine status type for styling
          let statusType = "default";
          
          if (status === "COMPLETED") statusType = "success";
          else if (status === "FAILED" || status === "CANCELLED") statusType = "error";
          else if (status === "COMPLETED_WITH_ERRORS") statusType = "warning";
          else if (status === "QUEUED" || status.includes("PROCESSING")) statusType = "processing";
          
          // Normalize the status message
          const normalizedMessage = normalizeStatusMessage(message, status);
          
          // Format the start time more robustly
          let formattedStartTime = 'Unknown';
          const startTimeValue = job.startTime || job.created || job.timestamp;
          if (startTimeValue) {
            try {
              formattedStartTime = new Date(startTimeValue).toLocaleString();
            } catch (e) {
              // Silently handle date parsing errors
            }
          }
          
          // Merge the job object with processed status data AND progress data
          const finalJob = {
            ...job,
            ...fullData,
            ...progressData, // Include calculated progress data
            status, // Use processed status
            message, // Use processed message
            statusDisplay: status,
            statusType,
            normalizedMessage,
            displayName: job.operationId || 'Unknown Job',
            formattedStartTime,
            // Ensure jobType is preserved from the original job data
            jobType: job.jobType || fullData.jobType || 'Unknown'
          };
          
          // Job processing complete
          
          return finalJob;
        });
        
        setRecentJobs(formattedJobs);
        recentJobsRef.current = formattedJobs;
      } else {
        console.error("Failed to fetch recent jobs. Server response:", response.data);
        setRecentJobsError(response.data.error || "Failed to fetch recent jobs");
      }
    } catch (error: any) {
      console.error("Error fetching recent jobs:", error);
      
      // Detailed error handling
      if (error.response) {
        setRecentJobsError(`Server error: ${error.response.status} - ${error.response.data?.error || 'Unknown error'}`);
      } else if (error.request) {
        setRecentJobsError("Network error: No response received from server");
      } else {
        setRecentJobsError(`Error: ${error.message}`);
      }
    } finally {
      setLoadingRecentJobs(false);
    }
  };
  
  // Handle search from the search bar
  const handleSearch = (value: string) => {
    if (value) {
      // Clear any existing interval FIRST before switching jobs
      if (intervalIdRef.current) {
        console.log(`🛑 Clearing existing interval for previous job: ${activeJobId}`);
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
      
      setActiveJobId(value);
      activeJobIdRef.current = value;
      fetchJobStatus(value);
      setupRefreshInterval(value, propRefreshInterval);
    }
  };
  
  // Set up the refresh interval
  const setupRefreshInterval = (id: string, interval: number) => {
    // Don't set up an interval for completed jobs
    if (jobData && isJobCompleted(jobData) && id === activeJobId && jobData.status !== 'PROCESSING') {
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
      return;
    }
    
    // Clear any existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    
    
    // Set up auto-refresh
    const newIntervalId = setInterval(() => {
      fetchJobStatus(activeJobIdRef.current);
    }, interval);
    
    setIntervalId(newIntervalId);
    intervalIdRef.current = newIntervalId;
  };

  // Utility function to detect completed state
  const isJobCompleted = (job: any): boolean => {
    if (!job) return false;
    
    // Simplified terminal states - matches backend normalization
    const completedStates = ['COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'CANCELLED'];
    return typeof job === 'object' && job.status && completedStates.includes(job.status);
  };

  // Utility function to get progress bar status based on success/failure ratio
  const getProgressStatus = (job: any): "success" | "exception" | "active" => {
    if (!job) return "active";
    
    // Check explicit failure statuses first
    if (job.status?.includes("FAILED") || job.status === "CANCELLED") {
      return "exception";
    }
    
    // If job is completed, check success ratio
    if (isJobCompleted(job) || job.percentComplete === 100) {
      const successCount = job.successCount || job.successfulCount || 0;
      const failureCount = job.failureCount || job.failedRecords?.length || 0;
      const totalRecords = job.totalRecords || 0;
      
      // If we have specific counts, use them to determine status
      if (totalRecords > 0 || (successCount + failureCount > 0)) {
        const failureRate = totalRecords > 0 ? failureCount / totalRecords : failureCount / (successCount + failureCount);
        
        if (failureRate >= 1.0) {
          // All records failed
          return "exception";
        } else if (failureRate > 0.1) {
          // More than 10% failure rate - show as warning (we'll use exception since there's no warning status)
          return "exception";
        } else {
          // Success with acceptable failure rate
          return "success";
        }
      }
      
      // Default to success for completed jobs without clear failure indicators
      return "success";
    }
    
    // Job is still active
    return "active";
  };
  
  // Fetch job status
  const fetchJobStatus = async (id: string = activeJobId) => {
    if (!id || typeof id !== 'string' || id.length > 100 || id.includes('<')) {
      console.error("Invalid job ID:", id);
      setError("Invalid job ID format");
      setLoading(false);
      return;
    }
    
    // Check if we already have data for this job to avoid unnecessary flashing
    const previousJob = jobData;
    const isSameJob = previousJob && id === activeJobId;
    
    // Check if job is in a terminal state that shouldn't be refreshed
    const isCompletedJob = isJobCompleted(previousJob);
    
    // If job is already completed and we're just refreshing the display, skip everything
    if (isSameJob && isCompletedJob && !loading) {
      console.log(`Job ${id} is already in completed state (${previousJob.status}). Skipping all updates.`);
      return;
    }

    // Ensure API base URL is present
    const baseUrl = process.env.REACT_APP_BACK_END_API || '';
    if (!baseUrl) {
      console.error("API base URL is not defined");
      setError("API configuration error");
      setLoading(false);
      return;
    }
    
    // Only show loading indicator if we don't already have data or it's a different job
    if (!isSameJob) {
      setLoading(true);
    }
    try {
      const fullUrl = `${process.env.REACT_APP_BACK_END_API}/salesforce/operation-status/${id}`;

      // Add a unique query parameter to track this specific request
      const response = await authAxios({
        method: "GET",
        url: `${fullUrl}?debug=true`,
      });
      
      // API Response check
      
      
      if (response.data && response.data.status) {
        // Use shared status processing function for consistency
        const { status, message, fullData } = processJobStatus(response.data, id, 'individual');
        let newData = { ...fullData, status, message };
        
        // Removed verbose progress logging
        
        // Check status history for debouncing - but allow progress updates for PROCESSING status
        const shouldUpdate = shouldUpdateStatus(id, newData.status);
        const isProgressUpdate = (
          newData.status.includes('PROCESSING') || 
          newData.status.includes('UPLOADING') || 
          newData.status === 'PROCESSING'
        ) && (
          newData.processedRecords !== jobData?.processedRecords || 
          newData.percentComplete !== jobData?.percentComplete ||
          newData.message !== jobData?.message
        );
        
        if (!shouldUpdate && !isProgressUpdate) {
          // Allow all updates - debouncing removed for better UX
        }
        
        // Update status history
        setStatusHistory(prev => ({
          ...prev,
          [id]: [...(prev[id] || []), newData.status].slice(-5) // Keep last 5 statuses
        }));
        
        // Removed verbose backend response logging
        
        // ENHANCED FIX: Handle Redis race condition where status gets stuck as PENDING/QUEUED
        // BUT: Only apply aggressive corrections if the message is generic, preserve detailed progress messages
        if (newData.status === 'PENDING' || newData.status === 'QUEUED') {
          const isGenericMessage = !newData.message || 
            newData.message === 'Starting data processing...' ||
            newData.message === 'Job queued for processing' ||
            newData.message === 'Job created' ||
            newData.message.length < 10; // Very short messages are likely generic
          
          // Status analysis for correction logic
          
          // Only apply aggressive correction if message is generic AND we have completion indicators
          if (isGenericMessage) {
            // Check for completion indicators (AGGRESSIVE detection)
            const hasCompletionMessage = newData.message && (
              newData.message.toLowerCase().includes('all records processed successfully') || 
              newData.message.toLowerCase().includes('completed successfully') ||
              newData.message.toLowerCase().includes('operation completed successfully') ||
              newData.message.toLowerCase().includes('processing completed') ||
              newData.message.toLowerCase().includes('upload complete') ||
              newData.message.toLowerCase().includes('successfully processed')
            );
            
            const hasCompletionData = (
              (newData.percentComplete === 100) ||
              (newData.endTime && newData.totalRecords > 0) ||
              (newData.successCount > 0 && newData.totalRecords > 0 && (newData.successCount + (newData.failureCount || 0)) === newData.totalRecords) ||
              (newData.processedRecords > 0 && newData.totalRecords > 0 && newData.processedRecords === newData.totalRecords) ||
              (newData.salesforceSuccessCount > 0 && newData.salesforceSuccessCount === newData.totalRecords) ||
              (newData.snowflakeSuccessCount > 0 && newData.snowflakeSuccessCount === newData.totalRecords) ||
              (newData.salesforcePending === false && newData.snowflakeComplete === true && newData.processingComplete === true)
            );
            
            // Check for processing indicators (should be PROCESSING, not PENDING)
            const hasProcessingMessage = newData.message && (
              newData.message.toLowerCase().includes('uploading data to salesforce') ||
              newData.message.toLowerCase().includes('starting salesforce upload') ||
              newData.message.toLowerCase().includes('executing snowflake') ||
              newData.message.toLowerCase().includes('snowflake processing') ||
              newData.message.toLowerCase().includes('processing batch') ||
              newData.message.toLowerCase().includes('uploading to snowflake') ||
              newData.message.toLowerCase().includes('processing your data')
            );
            
            const hasProcessingData = (
              (newData.percentComplete > 0 && newData.percentComplete < 100) ||
              (newData.processedRecords > 0 && newData.totalRecords > 0 && newData.processedRecords < newData.totalRecords) ||
              (newData.successCount > 0 && newData.totalRecords > 0 && (newData.successCount + (newData.failureCount || 0)) < newData.totalRecords)
            );
            
            if (hasCompletionMessage || hasCompletionData) {
              
              // Frontend correction
              newData.status = 'COMPLETED';
              newData.percentComplete = 100;
              newData.endTime = newData.endTime || new Date().toISOString();
              newData.lastUpdated = new Date().toISOString();
              
              // Backend correction - fire and forget
              (async () => {
                try {
                  await authAxios({
                    method: "POST",
                    url: `${process.env.REACT_APP_BACK_END_API}/salesforce/fix-stuck-job/${id}`,
                    data: { targetStatus: 'COMPLETED' }
                  });
                } catch (error) {
                  console.warn(`⚠️ Backend correction failed for ${id}:`, error);
                }
              })();
            } else if (hasProcessingMessage || hasProcessingData) {
              newData.status = 'PROCESSING';
              newData.lastUpdated = new Date().toISOString();
            }
          }
        }
        
        // ADDITIONAL FIX: Handle incorrect FAILED status on successfully completed jobs
        if (newData.status === 'FAILED') {
          // Check for false positive FAILED status
          
          // Check if this is actually a successful completion mislabeled as FAILED
          const hasSuccessMessage = newData.message && (
            newData.message.toLowerCase().includes('all records processed successfully') || 
            newData.message.toLowerCase().includes('completed successfully') ||
            newData.message.toLowerCase().includes('operation completed successfully') ||
            newData.message.toLowerCase().includes('successfully processed')
          );
          
          const hasSuccessData = (
            (newData.percentComplete === 100 && newData.successCount > 0 && (newData.failureCount === 0 || !newData.failureCount)) ||
            (newData.totalRecords > 0 && newData.successCount === newData.totalRecords && (newData.failureCount === 0 || !newData.failureCount)) ||
            (newData.salesforceSuccessCount > 0 && newData.salesforceSuccessCount === newData.totalRecords && (newData.salesforceFailureCount === 0 || !newData.salesforceFailureCount))
          );
          
          if (hasSuccessMessage || hasSuccessData) {
            newData.status = 'COMPLETED';
            newData.percentComplete = 100;
            newData.endTime = newData.endTime || new Date().toISOString();
            newData.lastUpdated = new Date().toISOString();
          }
        }
        
        // Removed verbose individual job API logging
        
        // Simple protection: Don't let completed jobs regress to QUEUED
        if (previousJob && isJobCompleted(previousJob) && newData.status === 'QUEUED') {
          newData.status = previousJob.status;
          newData.message = previousJob.message || newData.message;
        }
        
        // Removed status override logic - trust backend completely
        
        // For completed jobs, only preserve data if status hasn't changed
        if (isSameJob && previousJob && isJobCompleted(previousJob) && previousJob.status === newData.status) {
          // Same completed status - preserve existing data to prevent flipping
          newData = {
            ...previousJob,
            // Update timestamps and essential fields
            lastUpdated: newData.lastUpdated || new Date().toISOString(),
            message: newData.message || previousJob.message
          };
        } else if (isJobCompleted(newData)) {
          // New completion or status change - ensure proper completion data
          newData.percentComplete = 100;
          newData.endTime = newData.endTime || new Date().toISOString();
        }
        
        // ENHANCED: Calculate percentComplete using multiple fallbacks
        if (!newData.percentComplete || newData.percentComplete === 0) {
          let calculatedPercent = 0;
          
          // Try different combinations to calculate progress
          if (newData.totalRecords > 0) {
            // Try different combinations to calculate progress
            if (newData.processedRecords !== undefined && newData.processedRecords > 0) {
              calculatedPercent = Math.round((newData.processedRecords / newData.totalRecords) * 100);
            } else if (newData.successCount !== undefined && newData.successCount >= 0) {
              const processed = newData.successCount + (newData.failureCount || 0);
              calculatedPercent = Math.round((processed / newData.totalRecords) * 100);
            } else if (newData.successfulCount !== undefined && newData.successfulCount >= 0) {
              const processed = newData.successfulCount + (newData.failureCount || 0);
              calculatedPercent = Math.round((processed / newData.totalRecords) * 100);
            }
          }
          
          // Apply calculated percentage if we got a valid result
          if (calculatedPercent > 0 && calculatedPercent <= 100) {
            newData.percentComplete = calculatedPercent;
          }
        }
        
        // Removed verbose job data logging
        
        // Force React re-render by creating a new object reference
        const forceUpdate = { ...newData, lastUpdateTimestamp: Date.now() };
        setJobData(forceUpdate);
        setError(null);
        
        // EFFICIENT SYNC: Update the recent jobs table when active job updates
        if (activeJobId === id) {
          setRecentJobs(prev => {
            const jobIndex = prev.findIndex(job => job.operationId === id);
            if (jobIndex >= 0) {
              // Update existing job with new data while preserving table-specific fields
              const updated = [...prev];
              
              updated[jobIndex] = { 
                ...updated[jobIndex], // Preserve existing fields like formattedStartTime, jobType, etc.
                ...newData,          // Apply new data from individual API
                statusDisplay: newData.status,
                message: newData.message,
                status: newData.status,
                percentComplete: newData.percentComplete !== undefined ? newData.percentComplete : (updated[jobIndex].percentComplete || 0),
                processedRecords: newData.processedRecords !== undefined ? newData.processedRecords : (updated[jobIndex].processedRecords || 0),
                totalRecords: newData.totalRecords !== undefined ? newData.totalRecords : (updated[jobIndex].totalRecords || 0),
                // Force React to re-render by updating a timestamp
                syncTimestamp: Date.now(),
                successCount: newData.successCount !== undefined ? newData.successCount : updated[jobIndex].successCount || 0,
                failureCount: newData.failureCount !== undefined ? newData.failureCount : updated[jobIndex].failureCount || 0,
                lastUpdated: new Date().toISOString(),
                // Preserve essential display fields
                operationId: id,
                displayName: updated[jobIndex].displayName || id
              };
              
              // Job updated in recent jobs table
              return updated;
            } else {
              // Job not in recent jobs - trigger a fresh fetch
              fetchRecentJobs();
              return prev;
            }
          });
        }
        
        // Check if job is completed and clear interval if so
        if (isJobCompleted(newData)) {
          // Refresh recent jobs table one final time when job completes
          fetchRecentJobs();
          
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
            setIntervalId(null);
          }
        }
      } else {
        setError("Invalid response format");
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch job status");
    } finally {
      setLoading(false);
    }
  };
  
  // Keep recentJobsRef in sync with recentJobs state
  useEffect(() => {
    recentJobsRef.current = recentJobs;
  }, [recentJobs]);
  
  // Initialize from props when component mounts or propJobId changes
  useEffect(() => {
    // Always clear existing intervals first when propJobId changes
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    
    if (propJobId) {
      setActiveJobId(propJobId);
      activeJobIdRef.current = propJobId;
      fetchJobStatus(propJobId);
      
      // Always set up interval for new jobs - let setupRefreshInterval decide if needed
      setupRefreshInterval(propJobId, propRefreshInterval);
    }
    
    // Always fetch recent jobs regardless of whether we have a job ID
    fetchRecentJobs();
    
    // Cleanup intervals on unmount (removed constant recent jobs polling)
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [propJobId, propRefreshInterval]);

  // Get status badge
  const getStatusBadge = () => {
    if (!jobData) return <Badge status="default" text="Unknown" />;
    
    const status = jobData.status.toUpperCase();
    
    if (status === "COMPLETED") {
      return <Badge status="success" text="Completed" />;
    } else if (status === "FAILED") {
      return <Badge status="error" text="Failed" />;
    } else if (status === "CANCELLED") {
      return <Badge status="error" text="Cancelled" />;
    } else if (status === "PENDING" || status === "QUEUED") {
      return <Badge status="warning" text="Pending" />;
    } else if (status === "PROCESSING" || status.includes("PROCESSING")) {
      return <Badge status="processing" text="Processing" />;
    } else if (status === "COMPLETED_WITH_ERRORS") {
      return <Badge status="warning" text="Completed with Errors" />;
    } else {
      return <Badge status="default" text={status} />;
    }
  };
  
  // NEW: Function to select a job from the recent jobs list
  const selectJob = (jobId: string) => {
    // If selecting a job we're already monitoring, don't reset everything
    if (jobId === activeJobId) {
      console.log(`Already monitoring job ${jobId}`);
      return;
    }
    
    // Clear any existing interval FIRST before switching jobs
    if (intervalIdRef.current) {
      console.log(`🛑 Clearing existing interval for previous job: ${activeJobId}`);
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    
    // Reset for new job
    setSearchJobId("");
    
    // Find job in recent jobs list to pre-populate data
    const existingJobData = recentJobs.find(job => job.operationId === jobId);
    if (existingJobData) {
      // Pre-populate job data to avoid flickering and status changes
      setJobData(existingJobData);
    }
    
    // Update active job ID and start monitoring
    setActiveJobId(jobId);
    activeJobIdRef.current = jobId;
    fetchJobStatus(jobId);
    setupRefreshInterval(jobId, propRefreshInterval);
  };
  
  // Render the job history table with enhanced features
  const renderRecentJobs = () => {
    // Define enhanced table columns
    const columns = [
      {
        title: 'Job ID',
        dataIndex: 'operationId',
        key: 'operationId',
        width: 180,
        render: (text: string) => {
          // Show only the last part of the job ID for better readability
          const shortId = text.split('_').pop();
          return (
            <Button type="link" onClick={() => selectJob(text)}>
              {shortId}
            </Button>
          );
        },
      },
      {
        title: 'Type',
        dataIndex: 'jobType',
        key: 'jobType',
        width: 160,
        render: (jobType: string, record: any) => {
          // Use record.jobType as fallback if jobType param is undefined
          const actualJobType = jobType || record.jobType || 'Unknown';
          
          // Clean up job type display
          if (actualJobType === 'Add invoice # to Spot Data') {
            return <Tag color="blue">Invoice Update</Tag>;
          } else if (actualJobType?.includes('Spot Data')) {
            return <Tag color="green">Spot Data Upload</Tag>;
          } else {
            return <Tag color="default">{actualJobType}</Tag>;
          }
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 200,
        render: (_: any, record: any) => {
          const status = record.statusDisplay || record.status;
          const message = record.normalizedMessage || record.message;
          
          // Status rendering logic
          
          let color = 'default';
          let displayStatus = status;
          
          // Handle detailed processing statuses
          if (status === 'UPLOADING_TO_SNOWFLAKE') {
            color = 'processing';
            displayStatus = 'Uploading to Snowflake';
          } else if (status === 'SNOWFLAKE_PROCESSING' || status === 'PROCESSING_SNOWFLAKE') {
            color = 'processing';
            displayStatus = 'Processing in Snowflake';
          } else if (status === 'SNOWFLAKE_PROCESSED') {
            color = 'success';
            displayStatus = 'Snowflake Complete';
          } else if (status === 'SALESFORCE_PROCESSING') {
            color = 'processing';
            displayStatus = 'Uploading to Salesforce';
          } else if (status === 'COMPLETED') {
            color = 'success';
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            color = 'error';
          } else if (status === 'COMPLETED_WITH_ERRORS') {
            color = 'warning';
          } else if (status && (status.includes('PROCESSING') || status.includes('UPLOADING'))) {
            color = 'processing';
          } else if (status === 'QUEUED') {
            color = 'warning';
          }
          
          // Debug: final status display removed to reduce console spam
          
          return (
            <div>
              <Tag color={color}>{displayStatus}</Tag>
              {message && (
                <div style={{ fontSize: '12px', marginTop: '3px' }}>
                  {message}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: 'Progress',
        key: 'records',
        width: 140,
        render: (_: any, record: any) => {
          const successCount = record.successCount || 0;
          const failureCount = record.failureCount || 0;
          const processedRecords = record.processedRecords;
          const processed = processedRecords !== undefined && processedRecords !== null && !isNaN(processedRecords) 
            ? processedRecords 
            : (successCount + failureCount);
          const total = record.totalRecords || 0;
          const status = record.status || record.statusDisplay;
          
          if (total > 0) {
            // Show progress format based on status
            if (status === 'PROCESSING' || status?.includes('PROCESSING')) {
              return (
                <span>
                  Processing <span style={{ fontWeight: 'bold' }}>{(processed || 0).toLocaleString()}/{(total || 0).toLocaleString()}</span>
                </span>
              );
            } else if (status === 'COMPLETED' || status === 'COMPLETED_WITH_ERRORS') {
              const success = record.successCount || processed;
              const failure = record.failureCount || 0;
              return (
                <span>
                  <span style={{ color: '#52c41a' }}>{success.toLocaleString()}</span>
                  {failure > 0 && <span> / <span style={{ color: '#f5222d' }}>{failure.toLocaleString()}</span></span>}
                  <span> of {total.toLocaleString()}</span>
                </span>
              );
            } else {
              // Default format for other statuses
              return (
                <span>
                  <span style={{ fontWeight: 'bold' }}>{processed.toLocaleString()}/{total.toLocaleString()}</span>
                </span>
              );
            }
          }
          return status === 'PENDING' || status === 'QUEUED' ? 'Queued' : '-';
        },
      },
      {
        title: 'Started',
        key: 'startTime',
        width: 160,
        render: (_: any, record: any) => {
          // More robust date handling
          if (record.formattedStartTime && record.formattedStartTime !== 'Unknown') {
            return record.formattedStartTime;
          }
          
          // Try different date fields that might be available
          const dateStr = record.startTime || record.created || record.timestamp;
          if (dateStr) {
            try {
              return new Date(dateStr).toLocaleString();
            } catch (e) {
              console.log('Error parsing date:', dateStr, e);
            }
          }
          
          // For older jobs without start time but with end time, show "before completion"
          if (record.endTime) {
            return 'Before completion';
          }
          
          return '-';
        },
      },
      {
        title: 'Duration',
        key: 'duration',
        width: 120,
        render: (_: any, record: any) => {
          // If duration is provided directly from backend
          if (record.duration) {
            return formatElapsedTime(new Date(0), new Date(record.duration * 1000));
          }
          
          // Try different date fields to find a valid start time
          const startTimeValue = record.startTime || record.created || record.timestamp;
          if (startTimeValue) {
            try {
              const start = new Date(startTimeValue);
              const end = record.endTime ? new Date(record.endTime) : new Date();
              
              // Validate dates before calculating duration
              if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                // If we know times are estimated, show that
                if (record.hasEstimatedTimes) {
                  return `~${formatElapsedTime(start, end)} (est.)`;
                }
                return formatElapsedTime(start, end);
              }
            } catch (e) {
              console.log('Error calculating duration:', e);
            }
          }
          
          // For older completed jobs, show "Est. duration"
          if (record.endTime && record.totalRecords) {
            // Estimate processing time based on typical processing rates
            // Assume 5 records per second as a baseline
            const estimatedSeconds = Math.min(Math.max(record.totalRecords / 5, 10), 600);
            return `~${Math.round(estimatedSeconds)}s (est.)`;
          }
          
          return '-';
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 240,
        fixed: 'right' as const,
        render: (_: any, record: any) => {
          const hasFailedRecords = (record.status === 'COMPLETED_WITH_ERRORS' || record.status === 'FAILED') && 
                                  (record.failureCount > 0 || record.failedRecords?.length > 0);
          
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Button 
                type="primary" 
                size="small" 
                onClick={() => selectJob(record.operationId)}
              >
                {activeJobId === record.operationId ? 'Monitoring' : 'Monitor'}
              </Button>
              
              {/* Fix Job button */}
              <Button
                type="default"
                size="small"
                icon={<ToolOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  openFixJobModal(record);
                }}
                title="Fix stuck job"
              >
                Fix
              </Button>
              
              {/* Export Failed Records button - only show if there are failed records */}
              {hasFailedRecords && (
                <Button
                  type="default"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportFailedRecords(record.operationId);
                  }}
                  loading={loadingExport && selectedJobForExport === record.operationId}
                  title="Export failed records as CSV"
                >
                  Export
                </Button>
              )}
              
              {/* Reprocess Failed Records button - only show if there are failed records */}
              {hasFailedRecords && (
                <Button
                  type="default"
                  size="small"
                  icon={<RedoOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReprocessFailedRecords(record.operationId);
                  }}
                  loading={loadingReprocess && selectedJobForReprocess === record.operationId}
                  title="Reprocess failed records"
                >
                  Retry
                </Button>
              )}
            </div>
          );
        },
      },
    ];

    if (loadingRecentJobs && recentJobs.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin />
          <div style={{ marginTop: 10 }}>Loading recent jobs...</div>
        </div>
      );
    }
    
    if (recentJobsError) {
      return (
        <Alert 
          message="Error loading recent jobs" 
          description={recentJobsError}
          type="error" 
          showIcon 
          action={
            <Button size="small" onClick={fetchRecentJobs}>
              Retry
            </Button>
          }
        />
      );
    }
    
    if (recentJobs.length === 0) {
      return (
        <Alert
          message="No recent jobs found"
          description="Upload a file to create a new job or enter a job ID manually above."
          type="info"
          showIcon
        />
      );
    }

    return (
      <Card 
        title="Job History"
      >
        <Table 
          dataSource={recentJobs} 
          columns={columns} 
          rowKey="operationId"
          loading={loadingRecentJobs}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 'max-content' }}
          rowClassName={(record) => activeJobId === record.operationId ? 'ant-table-row-selected' : ''}
          onRow={(record) => ({
            onClick: () => selectJob(record.operationId),
            style: { cursor: 'pointer' }
          })}
          style={{ whiteSpace: 'nowrap' }}
        />
      </Card>
    );
  };
  
  // Render compact view if requested
  if (compact) {
    return (
      <Card bordered>
        {loading && !jobData ? (
          <Spin />
        ) : error ? (
          <Alert message={error} type="error" showIcon />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>Status: {getStatusBadge()}</div>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => fetchJobStatus()}
                size="small"
              >
                Refresh
              </Button>
            </div>
            
            {jobData?.percentComplete !== undefined && (
              <Progress 
                percent={isJobCompleted(jobData) ? 100 : jobData.percentComplete} 
                status={getProgressStatus(jobData)}
              />
            )}
            
            {jobData?.message && (
              <div style={{ marginTop: 10 }}>
                <Alert 
                  message={jobData.message} 
                  type={jobData.status === 'FAILED' ? 'error' : (jobData.status === 'CANCELLED' ? 'warning' : 'info')} 
                  showIcon 
                  action={
                    jobData.status === 'CANCELLED' && (jobData.message?.includes('already being processed') || jobData.message?.includes('already being processed by another job')) ? (
                      <Button 
                        size="small" 
                        type="link"
                        onClick={async () => {
                          // In compact mode, clear locks directly without modal
                          await fixJobStatus('CLEAR_RECORDS', jobData);
                        }}
                      >
                        Clear Locks
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            )}
          </>
        )}
      </Card>
    );
  }
  
  // Standard full view
  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px' }}>
      {/* Fix Job Modal */}
      <Modal
        title="Fix Stuck Job"
        open={fixStuckJobModalVisible}
        onCancel={() => setFixStuckJobModalVisible(false)}
        footer={null}
      >
        <div>
          <p>Select a new status for job: <strong>{jobToFix?.operationId}</strong></p>
          <p>Current status: <strong>{jobToFix?.statusDisplay || jobToFix?.status}</strong></p>
          
          <Radio.Group 
            onChange={(e) => setSelectedStatus(e.target.value)} 
            value={selectedStatus} 
            style={{ marginBottom: 20 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Radio value="CANCELLED">Cancel Job</Radio>
              <Radio value="CLEAR_RECORDS">Clear Stuck Records</Radio>
              <Radio value="COMPLETED">Mark as Complete</Radio>
            </div>
          </Radio.Group>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setFixStuckJobModalVisible(false)}>Cancel</Button>
            <Button 
              type="primary" 
              danger 
              onClick={() => fixJobStatus(selectedStatus)} 
              loading={fixingJob}
            >
              Update Status
            </Button>
          </div>
        </div>
      </Modal>
      <h1>Salesforce Batch Job Monitor</h1>
      
      <Card bordered>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={18}>
            <Search
              placeholder="Enter Job ID"
              enterButton={<Button type="primary" icon={<SearchOutlined />}>Monitor</Button>}
              size="large"
              value={searchJobId}
              onChange={(e) => setSearchJobId(e.target.value)}
              onSearch={handleSearch}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchRecentJobs}
              loading={loadingRecentJobs}
              style={{ width: '100%', height: '40px' }}
            >
              Refresh Jobs
            </Button>
          </Col>
        </Row>
        
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginTop: 16, marginBottom: 16 }}
          />
        )}
        
        {loading && !jobData && activeJobId && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin size="large" />
            <div style={{ marginTop: 10 }}>Loading job details...</div>
          </div>
        )}
        
        {jobData && (
          <div style={{ marginTop: 20, marginBottom: 20 }}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Job: {activeJobId.split('_').pop()}</span>
                  {getStatusBadge()}
                </div>
              }
              extra={
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={() => fetchJobStatus()}
                  size="small"
                >
                  Refresh
                </Button>
              }
              bordered
            >
              {jobData.message && (
                <Alert
                  message={normalizeStatusMessage(jobData.message, jobData.status)}
                  type={jobData.status === 'CANCELLED' ? 'warning' : 'info'}
                  showIcon
                  style={{ marginBottom: 15 }}
                  action={
                    jobData.status === 'CANCELLED' && jobData.message?.includes('already being processed') ? (
                      <div style={{ display: 'flex', gap: 8, flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Button 
                          size="small" 
                          type="primary"
                          onClick={() => openFixJobModal(jobData)}
                          style={{ marginBottom: 4 }}
                        >
                          Clear Locked Schedules
                        </Button>
                        <div style={{ fontSize: '11px', color: '#666', textAlign: 'right' }}>
                          Clear locks, then retry upload
                        </div>
                      </div>
                    ) : undefined
                  }
                />
              )}
              
              {(() => {
                // Use jobData directly for progress bar - it gets updated every 5s
                const dataToUse = jobData;
                
                if (!dataToUse) return null;
                
                const processed = dataToUse.processedRecords !== undefined ? dataToUse.processedRecords : (dataToUse.successCount + (dataToUse.failureCount || 0));
                const total = dataToUse.totalRecords || 0;
                const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
                
                // Progress bar data ready
                
                return (
                  <Progress 
                    percent={percent}
                    status={getProgressStatus(dataToUse)}
                    strokeWidth={15}
                    style={{ marginBottom: 20 }}
                    showInfo={false}
                  />
                );
              })()}
              
              <Row gutter={[16, 16]}>
                {(() => {
                  const currentJobFromTable = recentJobs.find(job => job.operationId === activeJobId);
                  const dataToUse = jobData || currentJobFromTable;
                  
                  if (!dataToUse || dataToUse.totalRecords === undefined) return null;
                  
                  const successCount = dataToUse.successCount || 0;
                  const failureCount = dataToUse.failureCount || 0;
                  const processedRecords = dataToUse.processedRecords;
                  const processed = processedRecords !== undefined && processedRecords !== null && !isNaN(processedRecords) 
                    ? processedRecords 
                    : (successCount + failureCount);
                  const total = dataToUse.totalRecords || 0;
                  const percent = total > 0 && !isNaN(processed) ? Math.round((processed / total) * 100) : 0;
                  
                  return (
                    <>
                      <Col span={8}>
                        <Statistic 
                          title={dataToUse.status?.includes('SNOWFLAKE') ? "Snowflake Records" : "Records Processed"}
                          value={(processed || 0).toLocaleString()}
                          suffix={`/ ${(total || 0).toLocaleString()}`}
                          valueStyle={{ color: '#3f8600' }}
                          prefix={<CheckCircleOutlined />}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic 
                          title="Percentage Complete"
                          value={percent || 0}
                          suffix="%"
                          valueStyle={{ color: '#1890ff' }}
                          prefix={<LineChartOutlined />}
                        />
                      </Col>
                    </>
                  );
                })()}
                
                
                {jobData.currentBatch !== undefined && (
                  <Col span={8}>
                    <Statistic 
                      title="Current Batch" 
                      value={jobData.currentBatch} 
                      suffix={(jobData.batchesTotal || jobData.totalBatches) ? `/ ${jobData.batchesTotal || jobData.totalBatches}` : (jobData.currentBatch > 0 ? '/ ?' : '')}
                      valueStyle={{ color: '#722ed1' }}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Col>
                )}
              </Row>
              
              <Divider />
              
              <Descriptions title="Job Details" bordered column={1} size="small">
                <Descriptions.Item label="Status">{jobData.status}</Descriptions.Item>
                {jobData.startTime && (
                  <Descriptions.Item label="Start Time">
                    {new Date(jobData.startTime).toLocaleString()}
                  </Descriptions.Item>
                )}
                {jobData.endTime && (
                  <Descriptions.Item label="End Time">
                    {new Date(jobData.endTime).toLocaleString()}
                  </Descriptions.Item>
                )}
                {jobData.startTime && !jobData.endTime && (
                  <Descriptions.Item label="Elapsed Time">
                    {formatElapsedTime(new Date(jobData.startTime))}
                  </Descriptions.Item>
                )}
                {jobData.successCount !== undefined && jobData.failureCount !== undefined && (
                  <Descriptions.Item label="Success/Failure">
                    <span style={{ color: '#52c41a' }}>{jobData.successCount.toLocaleString()}</span> successful
                    {jobData.failureCount > 0 && <span>, <span style={{ color: '#f5222d' }}>{jobData.failureCount.toLocaleString()}</span> failed</span>}
                  </Descriptions.Item>
                )}
                
                {/* Display user-friendly error message if available */}
                {jobData.userFriendlyErrorMessage && (
                  <Descriptions.Item label="Error Details">
                    {jobData.userFriendlyErrorMessage}
                  </Descriptions.Item>
                )}
                
                {/* Display recommended action if available */}
                {jobData.recommendedAction && (
                  <Descriptions.Item label="Recommended Action">
                    <strong>{jobData.recommendedAction}</strong>
                  </Descriptions.Item>
                )}
                
                {/* Display error summary if available */}
                {jobData.errorSummary && jobData.errorSummary.length > 0 && (
                  <Descriptions.Item label="Error Categories">
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {jobData.errorSummary.map((error: any, idx: number) => (
                        <li key={idx}>
                          <strong>{error.type}{error.subType ? ` (${error.subType})` : ''}</strong>: {error.count} records
                          {error.message && (
                            <div style={{ fontSize: '12px', marginTop: '2px', color: '#666' }}>
                              &quot;{error.message}&quot;
                            </div>
                          )}
                          {error.affectedFields && error.affectedFields.length > 0 && (
                            <div style={{ fontSize: '12px', marginTop: '2px' }}>
                              Fields: {error.affectedFields.join(', ')}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Descriptions.Item>
                )}
                
                {/* Display failed record samples for debugging */}
                {jobData.failedRecordsSample && jobData.failedRecordsSample.length > 0 && (
                  <Descriptions.Item label="Failed Records Sample">
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {jobData.failedRecordsSample.map((record: any, idx: number) => (
                        <li key={idx} style={{ marginBottom: '8px' }}>
                          <strong>ID:</strong> {record.spotDataId}
                          {record.invoiceNumber && <span> | <strong>Invoice:</strong> {record.invoiceNumber}</span>}
                          <div style={{ fontSize: '12px', color: '#f5222d', marginTop: '2px' }}>
                            {record.error}
                            {record.errorCode && <span> (Code: {record.errorCode})</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Descriptions.Item>
                )}
                
                {/* Display error report details if available */}
                {jobData.errorReport && (
                  <Descriptions.Item label="Detailed Error Report">
                    <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {jobData.errorReport.errorCategories?.map((category: any, idx: number) => (
                          <li key={idx}>
                            <strong>{category.type}{category.subType ? ` (${category.subType})` : ''}</strong>: {category.count} records
                            <div style={{ fontSize: '12px', marginLeft: '10px' }}>
                              {category.message}
                              {category.samples && category.samples.length > 0 && (
                                <div style={{ marginTop: '5px' }}>
                                  Sample affected schedules: {category.samples.map((s: any) => s.schedule).filter(Boolean).join(', ')}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
              
              <div style={{ marginTop: 20, fontSize: 12, color: '#999', textAlign: 'right' }}>
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </Card>
          </div>
        )}
        
        {/* Always show recent jobs section */}
        <div style={{ marginTop: 20 }}>
          {renderRecentJobs()}
        </div>
      </Card>
    </div>
  );
};

export default JobProgressMonitor;